import React from "react";
import BaseWorkPermitInfo from "../../types/BaseWorkPermitInfo";
import Resources from "../../types/Resources";
import { getPermitIcon } from "../../icons";
import { permitPageUrl, versionedSequenceIdentifier } from "../../common/permitUtils";
import { Date_toDateString } from "../../common/dateUtils";
import Button from "../Common/Button";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getHighestPermitCategory } from "../../common/enums";
import resourceUtils from "../../common/resourceUtils";

type PermitTableRowProps = {
    permit: BaseWorkPermitInfo;
    resources: Resources;
    onDeleteButtonClick: () => void;
    onCopyButtonClick: () => void;
    onCheckoutButtonClick: () => void;
    onRenewPermitButtonClick: () => void;
    onMapButtonClick: () => void;
};

function PermitTableRow({
    permit,
    resources,
    onDeleteButtonClick,
    onCopyButtonClick,
    onCheckoutButtonClick,
    onRenewPermitButtonClick,
    onMapButtonClick
}: PermitTableRowProps) {
    const iconCategory = getHighestPermitCategory(permit.permitCategories);
    const isActive = permit.positions.some((p) => p.isActive);

    const { markerSrc } = getPermitIcon(
        permit.permitName,
        iconCategory,
        permit.status,
        isActive,
        permit.maritimeEventType
    );
    const sequenceIdentifier = versionedSequenceIdentifier(permit);
    const url = permitPageUrl({
        permitType: permit.permitName,
        departmentId: permit.departmentId,
        documentReferenceId: permit.documentReferenceId
    });

    const positionListItems = permit.positions.map((p) => <li key={p.id}>{p.comment}</li>);

    const permitNameResource = resourceUtils.getPermitNameResource(permit.permitName, permit.contentVersion, resources);

    return (
        <tr>
            <td>
                <img src={markerSrc} alt={permitNameResource} style={{width: 42}} />
            </td>
            <td className="text-nowrap">
                <a href={url}>{sequenceIdentifier}</a>
            </td>
            <td className="text-nowrap">{Date_toDateString(permit.startDate)}</td>
            <td className="text-nowrap">{Date_toDateString(permit.endDate)}</td>
            <td style={{ maxWidth: 220 }}>
                <ul className="list-unstyled">{positionListItems}</ul>
            </td>
            <td>{permit.companyNameSnapshot}</td>
            <td>{permit.departmentName}</td>
            <td>{permit.entrepreneur}</td>
            <td style={{ maxWidth: 400 }}>{permit.description}</td>
            <td>
                <div className="d-flex justify-content-end align-items-center">
                    {permit.hasAttachedFiles ? (
                        <OverlayTrigger
                            overlay={<Tooltip>{resources.get("HasAttachedFiles")}</Tooltip>}
                            delay={{ show: 200, hide: 0 }}
                        >
                            <div className="d-inline-block">
                                <i className="fas fa-paperclip-vertical m-2" style={{ fontSize: "20px" }}></i>
                            </div>
                        </OverlayTrigger>
                    ) : null}

                    {permit.canDelete ? (
                        <Button
                            className="me-1"
                            type="button"
                            variant="danger"
                            tooltip={resources.get("DeletePermit")}
                            onClick={onDeleteButtonClick}
                        >
                            <i className="fas fa-trash-can" style={{ color: "white" }}></i>
                        </Button>
                    ) : null}

                    {permit.canCopy ? (
                        <Button
                            className="me-1"
                            type="button"
                            variant="secondary"
                            tooltip={resources.get("CopyOldToNew")}
                            onClick={onCopyButtonClick}
                        >
                            <i className="fas fa-file"></i>
                        </Button>
                    ) : null}

                    {permit.canCheckout ? (
                        <Button
                            className="me-1"
                            variant="secondary"
                            tooltip={resources.get("CheckoutPermit")}
                            onClick={onCheckoutButtonClick}
                        >
                            <i className="fas fa-check"></i>
                        </Button>
                    ) : null}

                    {permit.canRenew ? (
                        <Button
                            className="me-1"
                            variant="secondary"
                            tooltip={resources.get("RenewPermit")}
                            onClick={onRenewPermitButtonClick}
                        >
                            <i className="fas fa-timer"></i>
                        </Button>
                    ) : null}

                    <Button variant="secondary" tooltip={resources.get("ShowOnMap")} onClick={onMapButtonClick}>
                        <i className="fas fa-location-dot"></i>
                    </Button>
                </div>
            </td>
        </tr>
    );
}

export default PermitTableRow;
